import React, { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Heading,
  Text,
  Button,
  useToast,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Badge,
  Card,
  CardBody,
  Divider,
  VStack,
  HStack,
  Tag,
  TagLabel,
  TagLeftIcon,
  List,
  ListItem,
  ListIcon,
  SimpleGrid,
  Flex,
  IconButton,
  Tooltip,
  useColorMode,
  Link
} from '@chakra-ui/react';
import {
  FaNewspaper,
  FaRss,
  FaBrain,
  FaBullseye,
  FaLightbulb,
  FaShieldAlt,
  FaExclamationTriangle,
  FaCheckCircle,
  FaArrowRight,
  FaClock,
  FaGlobe,
  FaChartLine,
  FaTools,
  FaExternalLinkAlt,
  FaDownload,
  FaBuilding,
  FaSearch
} from 'react-icons/fa';
import OpenAI from 'openai';
import companiesData from '../data/companies.json';

function NewsFeed({ apiKey }) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [analysis, setAnalysis] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { colorMode } = useColorMode();

  useEffect(() => {
    fetchNews();
    const interval = setInterval(fetchNews, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchNews = async () => {
    try {
      const response = await fetch('https://api.rss2json.com/v1/api.json?rss_url=https://thehackernews.com/feeds/posts/default');
      const data = await response.json();
      
      if (data.status === 'ok') {
        setNews(data.items.map(item => ({
          ...item,
          pubDate: new Date(item.pubDate),
        })));
      }
    } catch (error) {
      toast({
        title: 'Error fetching news',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const findRelevantCompanies = (analysis) => {
    const keywords = {
      'cloud': ['cloudSecurity', 'webAndInfrastructureSecurity'],
      'network': ['networkAndInfrastructureSecurity'],
      'endpoint': ['endpointSecurity'],
      'application': ['applicationSecurity'],
      'identity': ['authentication', 'idaas', 'consumerIdentity'],
      'access': ['privilegedAccessManagement', 'dataRightsSecurity'],
      'threat': ['threatIntelligence'],
      'iot': ['iotSecurity'],
      'email': ['messagingSecurity'],
      'data': ['dataSecurityEncryption', 'dataSecurityDLP', 'dataPrivacy'],
      'mobile': ['mobileSecurity'],
      'quantum': ['quantumSecurity'],
      'ai': ['aiSecurityAndMLOps', 'generativeAISecurity'],
      'supply chain': ['supplyChainSecurity'],
      'zero trust': ['zeroTrustArchitecture'],
      'xdr': ['extendedDetectionAndResponse'],
      'blockchain': ['blockchainSecurity'],
      'fraud': ['fraudAndTransactionSecurity']
    };

    const relevantCategories = new Set();
    const analysisLower = analysis.toLowerCase();

    Object.entries(keywords).forEach(([keyword, categories]) => {
      if (analysisLower.includes(keyword)) {
        categories.forEach(category => relevantCategories.add(category));
      }
    });

    const recommendations = Array.from(relevantCategories).reduce((acc, categoryKey) => {
      const category = companiesData.categories[categoryKey];
      if (category) {
        acc.push({
          category: category.name,
          companies: category.companies.slice(0, 5)
        });
      }
      return acc;
    }, []);

    return recommendations;
  };

  const analyzeArticle = async (article) => {
    if (!apiKey) {
      toast({
        title: 'API Key Required',
        description: 'Please set your OpenAI API key or enter a trial code first',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setSelectedArticle(article);
    setAnalyzing(true);
    onOpen();

    try {
      const openai = new OpenAI({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true
      });

      const response = await openai.chat.completions.create({
        model: "gpt-4.1-nano",
        messages: [{
          role: "user",
          content: `Analyze this cybersecurity news article and provide insights:
          
          Title: ${article.title}
          Content: ${article.description}
          
          Please provide analysis in the following format:

          # Executive Summary
          [2-3 sentence overview of the article's significance]

          ## Key Points 🎯
          • [Key point 1]
          • [Key point 2]
          • [Key point 3]

          ## Technical Impact 🛡️
          • [Technical detail 1]
          • [Technical detail 2]
          • [Technical detail 3]

          ## Industry Implications 📈
          • [Implication 1]
          • [Implication 2]
          • [Implication 3]

          ## Security Recommendations 🔒
          1. [Recommendation 1]
          2. [Recommendation 2]
          3. [Recommendation 3]

          ## Threat Analysis 🚨
          Risk Level: [Low/Medium/High]
          
          Affected Systems:
          • [System 1]
          • [System 2]
          
          Attack Vectors:
          • [Vector 1]
          • [Vector 2]

          ## Mitigation Steps 🛠️
          1. [Step 1]
          2. [Step 2]
          3. [Step 3]

          ## Future Implications 🔮
          • [Short-term impact]
          • [Long-term impact]
          • [Industry trends]

          ## Security Categories 🏢
          List the main security categories this incident relates to (e.g., Cloud Security, Network Security, etc.)`
        }],
      });

      const analysis = response.choices[0].message.content;
      setAnalysis(analysis);

      const recommendations = findRelevantCompanies(analysis);
      setRecommendations(recommendations);

    } catch (error) {
      toast({
        title: 'Analysis Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
    }
  };

  const getRiskLevelColor = (text) => {
    if (text.includes('High')) return 'red';
    if (text.includes('Medium')) return 'orange';
    if (text.includes('Low')) return 'green';
    return 'gray';
  };

  const exportAnalysis = () => {
    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${selectedArticle?.title} - Security Analysis</title>
          <meta charset="UTF-8">
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              line-height: 1.6;
              max-width: 1200px;
              margin: 0 auto;
              padding: 2rem;
              background: #f8f9fa;
              color: #1a202c;
            }
            .container {
              background: white;
              border-radius: 8px;
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
              padding: 2rem;
              margin-bottom: 2rem;
            }
            h1 {
              color: #2B6CB0;
              border-bottom: 2px solid #E2E8F0;
              padding-bottom: 0.5rem;
              margin-bottom: 1.5rem;
            }
            h2 {
              color: #2D3748;
              margin-top: 2rem;
              border-left: 4px solid #4299E1;
              padding-left: 1rem;
            }
            .card {
              background: #f8fafc;
              border: 1px solid #E2E8F0;
              border-radius: 8px;
              padding: 1.5rem;
              margin: 1rem 0;
            }
            .risk-badge {
              display: inline-block;
              padding: 0.25rem 0.75rem;
              border-radius: 9999px;
              font-weight: 500;
              font-size: 0.875rem;
              color: white;
              margin-left: 0.5rem;
            }
            .risk-high { background-color: #F56565; }
            .risk-medium { background-color: #ED8936; }
            .risk-low { background-color: #48BB78; }
            .bullet-point {
              display: flex;
              align-items: center;
              gap: 0.5rem;
              margin: 0.5rem 0;
              padding-left: 1rem;
            }
            .bullet-point::before {
              content: "→";
              color: #4299E1;
            }
            .meta-info {
              color: #718096;
              font-size: 0.875rem;
              margin-bottom: 1rem;
            }
            .recommendations {
              margin-top: 2rem;
              padding: 1.5rem;
              background: #EBF8FF;
              border-radius: 8px;
            }
            .company-badge {
              display: inline-block;
              padding: 0.25rem 0.75rem;
              background: #4299E1;
              color: white;
              border-radius: 9999px;
              margin: 0.25rem;
              font-size: 0.875rem;
            }
            .category-section {
              margin: 1rem 0;
            }
            .category-title {
              color: #2C5282;
              font-size: 1.1rem;
              font-weight: 600;
              margin-bottom: 0.5rem;
            }
            footer {
              text-align: center;
              margin-top: 2rem;
              padding-top: 1rem;
              border-top: 1px solid #E2E8F0;
              color: #718096;
              font-size: 0.875rem;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>🔒 Cybersecurity News Analysis</h1>
            
            <div class="meta-info">
              <strong>Article:</strong> ${selectedArticle?.title}<br>
              <strong>Published:</strong> ${selectedArticle?.pubDate}<br>
              <strong>Source:</strong> The Hacker News
            </div>

            ${analysis.split('\n\n').map(section => {
              if (section.startsWith('# ')) {
                return `<h1>${section.replace('# ', '')}</h1>`;
              } else if (section.startsWith('## ')) {
                const title = section.split('\n')[0].replace('## ', '');
                const content = section.split('\n').slice(1).join('\n');
                
                return `
                  <div class="card">
                    <h2>${title}</h2>
                    ${content.split('\n').map(line => {
                      if (line.startsWith('Risk Level:')) {
                        const level = line.split(':')[1].trim();
                        const riskClass = level.toLowerCase().includes('high') ? 'risk-high' : 
                                        level.toLowerCase().includes('medium') ? 'risk-medium' : 'risk-low';
                        return `
                          <p>
                            <strong>Risk Level:</strong>
                            <span class="risk-badge ${riskClass}">${level}</span>
                          </p>
                        `;
                      }
                      if (line.startsWith('Affected Systems:') || line.startsWith('Attack Vectors:')) {
                        return `<p><strong>${line}</strong></p>`;
                      }
                      if (line.startsWith('•')) {
                        return `<div class="bullet-point">${line.replace('•', '').trim()}</div>`;
                      }
                      if (line.match(/^\d+\./)) {
                        return `<div class="bullet-point">${line.replace(/^\d+\./, '').trim()}</div>`;
                      }
                      return `<p>${line}</p>`;
                    }).join('')}
                  </div>
                `;
              }
              return '';
            }).join('')}

            ${recommendations.length > 0 ? `
              <div class="recommendations">
                <h2>🔍 Recommended Solutions</h2>
                <p>Based on the analysis, here are relevant cybersecurity solutions that could help address the identified concerns:</p>
                
                ${recommendations.map(rec => `
                  <div class="category-section">
                    <div class="category-title">${rec.category}</div>
                    <div>
                      ${rec.companies.map(company => `
                        <span class="company-badge">${company}</span>
                      `).join('')}
                    </div>
                  </div>
                `).join('')}
              </div>
            ` : ''}

            <footer>
              Generated by Neatlabs™ Cyber & Digital Intelligence<br>
              ${new Date().toLocaleString()}
            </footer>
          </div>
        </body>
      </html>
    `;

    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedArticle?.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}-analysis.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: 'Analysis Exported',
      description: 'The analysis report has been downloaded successfully.',
      status: 'success',
      duration: 3000,
    });
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" />
        <Text mt={4}>Loading cybersecurity news...</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={4}>
        <Heading size="md" display="flex" alignItems="center" gap={2}>
          <FaNewspaper />
          Latest Cybersecurity News
        </Heading>
        {news.map((article) => (
          <Card key={article.guid}>
            <CardBody>
              <Stack spacing={3}>
                <Heading size="sm">{article.title}</Heading>
                <HStack spacing={4} color="gray.500">
                  <HStack>
                    <FaClock />
                    <Text fontSize="sm">{getTimeAgo(article.pubDate)}</Text>
                  </HStack>
                  <HStack>
                    <FaGlobe />
                    <Text fontSize="sm">The Hacker News</Text>
                  </HStack>
                </HStack>
                <Text noOfLines={3}>{article.description}</Text>
                <Divider />
                <Stack direction="row" spacing={4}>
                  <Button
                    leftIcon={<FaBrain />}
                    onClick={() => analyzeArticle(article)}
                    colorScheme="blue"
                    size="sm"
                  >
                    Analyze
                  </Button>
                  <Button
                    leftIcon={<FaRss />}
                    as="a"
                    href={article.link}
                    target="_blank"
                    variant="outline"
                    size="sm"
                  >
                    Read Full Article
                  </Button>
                </Stack>
              </Stack>
            </CardBody>
          </Card>
        ))}
      </Stack>

      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={2}>
              <FaNewspaper />
              <Text>{selectedArticle?.title}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {analyzing ? (
              <Flex direction="column" align="center" justify="center" py={10}>
                <Spinner size="xl" thickness="4px" />
                <Text mt={4} fontSize="lg">Analyzing article...</Text>
                <Text fontSize="sm" color="gray.500">Using AI to extract insights</Text>
              </Flex>
            ) : (
              <VStack spacing={6} align="stretch">
                {analysis.split('\n\n').map((section, index) => {
                  if (section.startsWith('# ')) {
                    return (
                      <Box key={index}>
                        <Heading size="lg" mb={4}>
                          {section.replace('# ', '')}
                        </Heading>
                      </Box>
                    );
                  } else if (section.startsWith('## ')) {
                    const title = section.split('\n')[0].replace('## ', '');
                    const content = section.split('\n').slice(1).join('\n');
                    
                    const icon = {
                      'Key Points 🎯': FaBullseye,
                      'Technical Impact 🛡️': FaTools,
                      'Industry Implications 📈': FaChartLine,
                      'Security Recommendations 🔒': FaShieldAlt,
                      'Threat Analysis 🚨': FaExclamationTriangle,
                      'Mitigation Steps 🛠️': FaCheckCircle,
                      'Future Implications 🔮': FaLightbulb,
                      'Security Categories 🏢': FaBuilding
                    }[title] || FaNewspaper;

                    return (
                      <Card key={index} variant="elevated">
                        <CardBody>
                          <VStack align="stretch" spacing={4}>
                            <HStack>
                              <Box as={icon} />
                              <Heading size="md">{title}</Heading>
                            </HStack>
                            <Box pl={6}>
                              {content.split('\n').map((line, i) => {
                                if (line.startsWith('Risk Level:')) {
                                  const level = line.split(':')[1].trim();
                                  return (
                                    <HStack key={i} mb={4}>
                                      <Text fontWeight="bold">Risk Level:</Text>
                                      <Badge colorScheme={getRiskLevelColor(level)}>
                                        {level}
                                      </Badge>
                                    </HStack>
                                  );
                                }
                                if (line.startsWith('Affected Systems:') || 
                                    line.startsWith('Attack Vectors:')) {
                                  return (
                                    <Box key={i} mb={2}>
                                      <Text fontWeight="bold" mb={2}>{line}</Text>
                                    </Box>
                                  );
                                }
                                if (line.startsWith('•')) {
                                  return (
                                    <HStack key={i} mb={2} ml={4}>
                                      <Box as={FaArrowRight} size="12px" />
                                      <Text>{line.replace('•', '').trim()}</Text>
                                    </HStack>
                                  );
                                }
                                if (line.match(/^\d+\./)) {
                                  return (
                                    <HStack key={i} mb={2} ml={4}>
                                      <Box as={FaCheckCircle} size="12px" />
                                      <Text>{line.replace(/^\d+\./, '').trim()}</Text>
                                    </HStack>
                                  );
                                }
                                return <Text key={i} mb={2}>{line}</Text>;
                              })}
                            </Box>
                          </VStack>
                        </CardBody>
                      </Card>
                    );
                  }
                  return null;
                })}

                {recommendations && recommendations.length > 0 && (
                  <Card variant="elevated">
                    <CardBody>
                      <VStack align="stretch" spacing={4}>
                        <HStack>
                          <Box as={FaSearch} />
                          <Heading size="md">Recommended Solutions</Heading>
                        </HStack>
                        <Box>
                          {recommendations.map((rec, index) => (
                            <Box key={index} mb={4}>
                              <Heading size="sm" mb={2}>
                                {rec.category}
                              </Heading>
                              <Flex wrap="wrap" gap={2}>
                                {rec.companies.map((company, compIndex) => (
                                  <Badge
                                    key={compIndex}
                                    colorScheme="blue"
                                    px={3}
                                    py={1}
                                    borderRadius="full"
                                    cursor="pointer"
                                    onClick={() => {
                                      toast({
                                        title: company,
                                        description: `View detailed analysis of ${company}`,
                                        status: 'info',
                                        duration: 3000,
                                      });
                                    }}
                                  >
                                    {company}
                                  </Badge>
                                ))}
                              </Flex>
                            </Box>
                          ))}
                        </Box>
                      </VStack>
                    </CardBody>
                  </Card>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              leftIcon={<FaDownload />}
              onClick={exportAnalysis}
              variant="cyber"
              isDisabled={analyzing || !analysis}
            >
              Export Analysis
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default NewsFeed;